import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Plus, Trash2, Loader2, Save, X, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import YoutubeMoviesAdmin from './YoutubeMoviesAdmin';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PLACEMENTS = [
  { value: 'home_top', label: 'Home — top' },
  { value: 'home_middle', label: 'Home — middle' },
  { value: 'search_top', label: 'Search — top' },
  { value: 'mylist_top', label: 'My List — top' },
  { value: 'kids_top', label: 'Kids — top' },
];

const emptyForm = {
  id: null,
  title: '',
  subtitle: '',
  image_url: '',
  link_url: '',
  cta: 'Learn more',
  placement: 'home_middle',
  active: true,
};

const Admin = () => {
  const navigate = useNavigate();
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/sponsorship/all`);
      setAds(data.ads || []);
    } catch (e) {
      console.error('[Admin] load failed', e);
    } finally { setLoading(false); }
  };

  useEffect(() => { refresh(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.title.trim() || !form.image_url.trim()) {
      setError('Title and Image URL are required.');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      delete payload.id;
      if (form.id) {
        await axios.patch(`${API}/sponsorship/${form.id}`, payload);
      } else {
        await axios.post(`${API}/sponsorship`, payload);
      }
      setForm(emptyForm);
      refresh();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Save failed.');
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this sponsorship ad?')) return;
    // Optimistic UI removal so the list updates instantly even if the request is slow.
    const backup = ads;
    setAds((prev) => prev.filter((a) => a.id !== id));
    try {
      await axios.delete(`${API}/sponsorship/${id}`);
      await refresh();
    } catch (e) {
      console.error('[Admin] delete failed', e);
      setAds(backup);
    }
  };

  const edit = (ad) => setForm({ ...ad });

  return (
    <div className="min-h-screen bg-[#141414] text-white">
      <div className="px-6 md:px-14 py-6 border-b border-white/10 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/')} className="p-2 rounded hover:bg-white/10 transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-xl md:text-2xl font-bold">Sponsorship Admin</h1>
        </div>
        <span className="text-xs text-amber-400">Admin only</span>
      </div>

      <div className="grid md:grid-cols-2 gap-6 px-6 md:px-14 py-8 max-w-6xl mx-auto">
        <form onSubmit={submit} className="bg-[#1a1a1a] border border-white/10 rounded-lg p-6 h-fit">
          <h2 className="text-lg font-semibold mb-4">{form.id ? 'Edit ad' : 'New sponsorship ad'}</h2>

          <label className="block text-xs text-white/60 mb-1">Title</label>
          <input type="text" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full bg-[#333] rounded px-3 py-2 mb-3 outline-none focus:ring-2 focus:ring-white/40" />

          <label className="block text-xs text-white/60 mb-1">Subtitle</label>
          <input type="text" value={form.subtitle} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} className="w-full bg-[#333] rounded px-3 py-2 mb-3 outline-none focus:ring-2 focus:ring-white/40" />

          <label className="block text-xs text-white/60 mb-1">Image URL</label>
          <input type="text" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} className="w-full bg-[#333] rounded px-3 py-2 mb-3 outline-none focus:ring-2 focus:ring-white/40" placeholder="https://..." />

          <label className="block text-xs text-white/60 mb-1">Link URL</label>
          <input type="text" value={form.link_url} onChange={(e) => setForm({ ...form, link_url: e.target.value })} className="w-full bg-[#333] rounded px-3 py-2 mb-3 outline-none focus:ring-2 focus:ring-white/40" placeholder="https://..." />

          <label className="block text-xs text-white/60 mb-1">CTA text</label>
          <input type="text" value={form.cta} onChange={(e) => setForm({ ...form, cta: e.target.value })} className="w-full bg-[#333] rounded px-3 py-2 mb-3 outline-none focus:ring-2 focus:ring-white/40" />

          <label className="block text-xs text-white/60 mb-1">Placement</label>
          <select value={form.placement} onChange={(e) => setForm({ ...form, placement: e.target.value })} className="w-full bg-[#333] rounded px-3 py-2 mb-3 outline-none">
            {PLACEMENTS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>

          <label className="inline-flex items-center gap-2 text-sm mb-4">
            <input type="checkbox" checked={!!form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="accent-[#e11d48]" />
            Active
          </label>

          {error && <p className="text-red-400 text-sm mb-2">{error}</p>}

          <div className="flex gap-2">
            <button type="submit" disabled={saving} className="flex items-center gap-1.5 bg-[#e11d48] hover:bg-[#c9163f] px-4 py-2 rounded font-semibold text-white disabled:opacity-60">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (form.id ? <Save className="w-4 h-4" /> : <Plus className="w-4 h-4" />)}
              {form.id ? 'Save' : 'Create'}
            </button>
            {form.id && (
              <button type="button" onClick={() => setForm(emptyForm)} className="flex items-center gap-1.5 px-3 py-2 rounded border border-white/20 text-white/80 hover:bg-white/10">
                <X className="w-4 h-4" /> Cancel
              </button>
            )}
          </div>
        </form>

        <div>
          <h2 className="text-lg font-semibold mb-4">All sponsorship ads ({ads.length})</h2>
          {loading ? (
            <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-white/60" /></div>
          ) : ads.length === 0 ? (
            <p className="text-white/60">No ads yet.</p>
          ) : (
            <div className="space-y-3">
              {ads.map((ad) => (
                <div key={ad.id} className="bg-[#1a1a1a] border border-white/10 rounded-lg overflow-hidden flex">
                  <img src={ad.image_url} alt={ad.title} className="w-32 h-24 object-cover flex-shrink-0" />
                  <div className="flex-1 p-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-semibold truncate">{ad.title}</div>
                        <div className="text-xs text-white/60 truncate">{ad.subtitle}</div>
                        <div className="text-[10px] uppercase tracking-widest text-emerald-400 mt-1">
                          {ad.placement}{!ad.active && ' · INACTIVE'}
                        </div>
                      </div>
                      <div className="flex flex-col gap-1">
                        <button onClick={() => edit(ad)} className="text-xs bg-white/10 hover:bg-white/20 px-2 py-1 rounded">Edit</button>
                        <button onClick={() => remove(ad.id)} className="text-xs bg-red-500/20 hover:bg-red-500/30 text-red-300 px-2 py-1 rounded inline-flex items-center gap-1">
                          <Trash2 className="w-3 h-3" /> Del
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="px-6 md:px-14 pb-16 max-w-6xl mx-auto">
        <YoutubeMoviesAdmin />
      </div>
    </div>
  );
};

export default Admin;
